"use client";

import type { Attachment, UIMessage } from "ai";
import type { UseChatHelpers } from "@ai-sdk/react";
import { AnimatePresence, motion } from "framer-motion";
import { Sparkles, X } from "lucide-react";
import {
  type Dispatch,
  type MutableRefObject,
  type SetStateAction,
  useCallback,
  useState,
} from "react";
import { usePathname, useSearchParams } from "next/navigation";
import { MultimodalInput } from "@/components/multimodal-input";
import type { AgentModelKey } from "@/lib/ai/agent-models";
import { parseAlertViewTab } from "@/components/alert-page/AlertViewSwitcher";
import type { RequestedDataSource } from "@/lib/data-catalog";
import type { VisibilityType } from "@/components/visibility-selector";
import type { PageContextPayload } from "@/lib/page-context-types";
import {
  buildPageContextPayload,
  captureAlertPanelScreenshot,
} from "@/lib/capturePageContext";
import { cn } from "@/lib/utils";

const PILL_LABEL = "Ask Xynth";

export function ContextualChatDock({
  chatId,
  input,
  setInput,
  handleSubmit,
  status,
  stop,
  attachments,
  setAttachments,
  messages,
  append,
  setMessages,
  selectedVisibilityType,
  dataRef,
  deepResearchRef,
  modelTierRef,
  modelTier,
  onModelTierChange,
  selectedModelKey,
  onModelChange,
  modelOverrideRef,
  isRerunning,
  isInitializing,
  editingCell,
  onClearEditingCell,
  isMobile,
  isAlertsRoute,
  alertId,
  alertName,
  alertIsOwner,
  alertVisitorMode,
  viewMode,
  onVisitorSubmitGate,
  onExpandChat,
  onPillClick,
  pageContextRef,
  placeholderTexts,
  promptPills,
  onPromptPillClick,
}: {
  chatId: string;
  input: UseChatHelpers["input"];
  setInput: UseChatHelpers["setInput"];
  handleSubmit: UseChatHelpers["handleSubmit"];
  status: UseChatHelpers["status"];
  stop: UseChatHelpers["stop"];
  attachments: Array<Attachment>;
  setAttachments: Dispatch<SetStateAction<Array<Attachment>>>;
  messages: Array<UIMessage>;
  append: UseChatHelpers["append"];
  setMessages: UseChatHelpers["setMessages"];
  selectedVisibilityType: VisibilityType;
  dataRef?: MutableRefObject<Array<RequestedDataSource>>;
  deepResearchRef?: MutableRefObject<boolean>;
  modelTierRef?: MutableRefObject<string>;
  modelTier?: "basic" | "max";
  onModelTierChange?: (tier: "basic" | "max") => void;
  selectedModelKey?: AgentModelKey;
  onModelChange?: (key: AgentModelKey) => void;
  modelOverrideRef?: MutableRefObject<string | undefined>;
  isRerunning?: boolean;
  isInitializing?: boolean;
  editingCell?: { index: number; purpose: string } | null;
  onClearEditingCell?: () => void;
  isMobile: boolean;
  isAlertsRoute: boolean;
  alertId?: string | null;
  alertName?: string | null;
  alertIsOwner?: boolean;
  alertVisitorMode?: boolean;
  viewMode?: string;
  onVisitorSubmitGate?: () => boolean;
  onExpandChat: () => void;
  /** When provided, clicking the pill runs this instead of opening the
   *  floating composer — used in a live build session to re-expand the real
   *  chat column rather than spawning a separate mini-chat. */
  onPillClick?: () => void;
  pageContextRef: MutableRefObject<PageContextPayload | null>;
  placeholderTexts?: string[];
  promptPills?: string[];
  onPromptPillClick?: (prompt: string) => Promise<string | null | undefined> | void;
}) {
  const [dockOpen, setDockOpen] = useState(false);
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const tab = parseAlertViewTab(searchParams?.get("tab") ?? null);
  const label = PILL_LABEL;
  const isSignal = viewMode === "signal";

  const runVisitorGate = useCallback((): boolean => {
    if (alertVisitorMode && onVisitorSubmitGate) {
      return onVisitorSubmitGate();
    }
    return true;
  }, [alertVisitorMode, onVisitorSubmitGate]);

  const prepareContextualSubmit = useCallback(async (): Promise<Array<Attachment>> => {
    console.info("[ContextualChatDock] page context capture start", {
      isAlertsRoute,
      alertId,
      alertName,
      tab,
      route: `${pathname}${searchParams?.toString() ? `?${searchParams}` : ""}`,
    });
    const shot = await captureAlertPanelScreenshot();
    pageContextRef.current = buildPageContextPayload({
      isAlertsRoute,
      alertId,
      alertName,
      alertTab: isAlertsRoute ? tab : undefined,
      isOwner: alertIsOwner,
      hasScreenshot: Boolean(shot),
      route: `${pathname}${searchParams?.toString() ? `?${searchParams}` : ""}`,
    });
    console.info("[ContextualChatDock] page context prepared", {
      hasScreenshot: Boolean(shot),
      attachmentCountBefore: attachments.length,
      attachmentCountAfter: shot ? attachments.length + 1 : attachments.length,
      pageContext: pageContextRef.current,
    });
    return shot ? [...attachments, shot] : attachments;
  }, [
    alertId,
    alertIsOwner,
    alertName,
    attachments,
    isAlertsRoute,
    pageContextRef,
    pathname,
    searchParams,
    tab,
  ]);

  const contextualAppend = useCallback<UseChatHelpers["append"]>(
    async (message, chatRequestOptions) => {
      if (!runVisitorGate()) {
        console.info("[ContextualChatDock] submit blocked by visitor gate");
        return Promise.resolve(null);
      }

      const mergedAttachments = await prepareContextualSubmit();
      onExpandChat();

      return append(message, {
        ...chatRequestOptions,
        experimental_attachments: mergedAttachments,
      });
    },
    [append, onExpandChat, prepareContextualSubmit, runVisitorGate],
  );

  const contextualPromptPillClick = useCallback(
    async (content: string) => {
      if (!runVisitorGate()) return Promise.resolve(null);
      const mergedAttachments = await prepareContextualSubmit();
      onExpandChat();
      return append(
        { role: "user", content },
        { experimental_attachments: mergedAttachments },
      );
    },
    [append, onExpandChat, prepareContextualSubmit, runVisitorGate],
  );

  return (
    <div
      data-tour="alert-chat-input"
      className={cn(
        // `fixed` (not `absolute`) so the dock anchors to the whole viewport
        // instead of the narrow right-hand alert panel. Desktop anchors the
        // pill bottom-RIGHT (floating action button position — far more
        // noticeable than bottom-center, and it never overlaps the chart).
        // Mobile keeps the full-width bottom-center anchor it always had.
        "pointer-events-none fixed inset-x-0 z-50 flex items-end px-4",
        isMobile ? "bottom-5 justify-center" : "bottom-7 justify-end pr-7",
      )}
    >
      <AnimatePresence mode="wait" initial={false}>
        {!dockOpen ? (
          <motion.button
            key="pill"
            type="button"
            initial={{ opacity: 0, y: 8, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 520, damping: 34 }}
            onClick={() => (onPillClick ? onPillClick() : setDockOpen(true))}
            className={cn(
              "group pointer-events-auto relative inline-flex items-center gap-2.5 rounded-full",
              "h-[52px] px-6 text-[15px] font-semibold tracking-[-0.01em]",
              "backdrop-blur-md transition hover:scale-[1.03] active:scale-[0.98]",
              isSignal
                ? "border border-emerald-300/40 bg-emerald-400/[0.14] text-emerald-50 shadow-[0_8px_30px_rgba(0,0,0,0.45),0_0_24px_rgba(52,211,153,0.25)] hover:border-emerald-300/65 hover:bg-emerald-400/25 hover:text-white hover:shadow-[0_8px_30px_rgba(0,0,0,0.45),0_0_36px_rgba(52,211,153,0.4)]"
                : "border border-sky-300/35 bg-sky-400/[0.12] text-sky-50 shadow-[0_8px_30px_rgba(0,0,0,0.45),0_0_24px_rgba(56,189,248,0.25)] hover:border-sky-300/60 hover:bg-sky-400/[0.22] hover:text-white hover:shadow-[0_8px_30px_rgba(0,0,0,0.45),0_0_36px_rgba(56,189,248,0.4)]",
            )}
          >
            {/* Slow idle pulse ring so the eye catches the button without
                it being obnoxious. Disappears on hover. */}
            <span
              aria-hidden
              className={cn(
                "absolute inset-0 -z-10 animate-ping rounded-full opacity-20 [animation-duration:2.6s] group-hover:hidden",
                isSignal ? "bg-emerald-400/40" : "bg-sky-400/40",
              )}
            />
            <Sparkles className={cn("size-5", isSignal ? "text-emerald-300" : "text-sky-300")} />
            <span className="truncate">{label}</span>
          </motion.button>
        ) : (
          <motion.div
            key="composer"
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 380, damping: 30 }}
            className="pointer-events-auto relative w-full max-w-lg"
          >
            <button
              type="button"
              aria-label="Close chat"
              onClick={() => setDockOpen(false)}
              className="absolute -right-2 -top-2 z-10 flex size-6 items-center justify-center rounded-full border border-white/10 bg-zinc-900 text-zinc-400 shadow-lg hover:bg-zinc-800 hover:text-white"
            >
              <X className="size-3.5" />
            </button>
            <div className="rounded-[22px] border border-white/10 bg-black/70 p-1 shadow-[0_20px_70px_rgba(0,0,0,0.45)] backdrop-blur-2xl">
              <MultimodalInput
                chatId={chatId}
                input={input}
                setInput={setInput}
                handleSubmit={handleSubmit}
                status={status}
                stop={stop}
                attachments={attachments}
                setAttachments={setAttachments}
                messages={messages}
                append={contextualAppend}
                setMessages={setMessages}
                selectedVisibilityType={selectedVisibilityType}
                dataRef={dataRef}
                deepResearchRef={deepResearchRef}
                modelTierRef={modelTierRef}
                modelTier={modelTier}
                onModelTierChange={onModelTierChange}
                selectedModelKey={selectedModelKey}
                onModelChange={onModelChange}
                modelOverrideRef={modelOverrideRef}
                isRerunning={isRerunning}
                isInitializing={isInitializing}
                editingCell={editingCell}
                onClearEditingCell={onClearEditingCell}
                placeholderTexts={placeholderTexts}
                promptPills={promptPills}
                onPromptPillClick={
                  onPromptPillClick
                    ? (p) => onPromptPillClick(p)
                    : promptPills
                      ? contextualPromptPillClick
                      : undefined
                }
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
